// EditAttachmentSidebar: Sidebar for editing attachment metadata (type, date, note, labels, access type, etc.)
import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { shortDateFormat, stringYMDFromDate, dayjsFromLocalDateString } from '@utils/dateWithTz';
import dayjs from 'dayjs';
import {
  AccessTypeEnum,
  AttachmentUploadedOutDTO,
  EntityTypeEnum,
  LabelPreviewWithoutTypeOutDTO,
} from '@api/ringil-api.v2';
import useForm, { proxiedPropertiesOf } from '@hooks/useForm/useForm';
import { InputDocumentType } from '@components/obsolete/Form/InputDocumentType/InputDocumentType.tsx';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import AddonsSvg from '@app/icons/AddonsSvg';
import { FiInfo } from 'react-icons/fi';
import LinkSvg from '@icons/LinkSvg';
import { HorizontalLine } from '@components/ringil3/styles.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import { findValidationMessage } from '@utils/validation.tsx';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import LabelsTag from '@components/framework/tags/LabelsTag.tsx';


const FileCard = styled.div`
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-family: 'DM Sans', sans-serif;
`;

const FileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: 'DM Sans', sans-serif;
`;

const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #343333;
  font-size: 0.9rem;
  font-weight: 400;
  font-family: 'DM Sans', sans-serif;
  padding: 0 12px;
`;

const FieldLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-family: 'DM Sans', sans-serif;
`;

const FieldLabel = styled.label`
  color: #616061;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: 'DM Sans', sans-serif;
`;

const InfoIcon = styled(FiInfo)`
  color: #b2b2b2;
  font-size: 0.9rem;
  cursor: pointer;
  font-family: 'DM Sans', sans-serif;
`;

const PillButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  font-family: 'DM Sans', sans-serif;
`;

const PillButton = styled.button<{ selected?: boolean; color?: string }>`
  border: none;
  border-radius: 24px;
  padding: 8px 24px;
  font-size: 0.9rem;
  font-weight: 500;
  background: ${({ selected }) => (selected ? '#fff5f0' : '#e7ebee')};
  color: ${({ selected }) => (selected ? '#fe5000' : '#616061')};
  border: 1px solid ${({ selected }) => (selected ? '#fe5000' : 'transparent')};
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'DM Sans', sans-serif;
  &:hover {
    background: ${({ selected }) => (selected ? '#ffe4d6' : '#e7ebee')};
  }
`;

const StyledTextarea = styled(InputTextarea)`
  width: 100%;
  min-height: 60px;
  border: 1px solid #616061;
  font-size: 1rem;
  color: #616061;
  background: #fff;
  padding: 10px 12px;
  font-family: 'DM Sans', sans-serif;
  &:focus {
    outline: none;
    border-color: #fe5000;
  }
`;

const StyledCalendar = styled(Calendar)`
  width: 100%;
  font-family: 'DM Sans', sans-serif;
  .p-inputtext {
    width: 100%;
    border: 1px solid #616061;
    font-size: 1rem;
    color: #616061;
    background: #fff;
    padding: 10px 12px;
    font-family: 'DM Sans', sans-serif;
    &:focus {
      outline: none;
      border-color: #fe5000;
    }
  }
  .p-calendar input.p-inputtext.p-component {
    border: 1px solid #616061 !important;
  }
  .p-calendar-w-btn .p-datepicker-trigger {
    background: transparent !important;
    border: none !important;
    color: #616061 !important;
    box-shadow: none !important;
  }
  .p-calendar-w-btn .p-datepicker-trigger:hover,
  .p-calendar-w-btn .p-datepicker-trigger:focus,
  .p-calendar-w-btn .p-datepicker-trigger:active {
    background: transparent !important;
    border: none !important;
    color: #616061 !important;
    box-shadow: none !important;
  }
`;

const StyledInputDocumentType = styled(InputDocumentType)`
  font-family: 'DM Sans', sans-serif;
  .p-dropdown.p-inputwrapper.p-component {
    border: 1px solid #616061 !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    font-size: 1rem;
    color: #616061;
    background: #fff;
    padding: 10px 12px;
    font-family: 'DM Sans', sans-serif;
    &:focus {
      outline: none;
      border-color: #fe5000;
    }
  }
`;

export interface EditAttachmentSidebarProps {
  visible: boolean;
  onHide: () => void;
  attachment: AttachmentUploadedOutDTO & { labels?: LabelPreviewWithoutTypeOutDTO[]; labelIds?: string[]; documentTypeName?: string; documentType?: any };
  onUpdated: (update: { attachmentId: string; changedDtoFields: Partial<AttachmentUploadedOutDTO>; newLabelIds: string[]; newLabels: LabelPreviewWithoutTypeOutDTO[] }) => void;
}

const EditAttachmentSidebar: React.FC<EditAttachmentSidebarProps> = ({ visible, onHide, attachment, onUpdated }) => {
  const { tr } = useTranslationLgs();
  const { editAttachment, cuGetLabelsForEntity } = useContext(ApiContext);

  const form = useForm<any>(
    yup.object().shape({
      documentType: yup
        .object()
        .shape({ documentType: yup.string().required(tr('validation.required', 'Value must be entered')) })
        .required(tr('validation.required', 'Value must be entered')),
      accessType: yup.string().required().oneOf(Object.keys(AccessTypeEnum)),
      userNote: yup.string().nullable().max(250, tr('validation.maxLength', 'Value must be at most {{max}} characters', { max: 250 })),
    }),
    {
      documentType: attachment.documentType ? { documentType: attachment.documentType, name: attachment.documentTypeName } : null,
      accessType: attachment.accessType,
      userDateLocalDate: attachment.userDateLocalDate,
      userNote: attachment.userNote,
      labels: [],
      labelIds: [],
    },
    data => save(data),
    false,
    false,
  );

  useEffect(() => {
    if (visible && attachment) {
      form.setValues({
        documentType: attachment.documentType ? { documentType: attachment.documentType, name: attachment.documentTypeName } : null,
        accessType: attachment.accessType,
        userDateLocalDate: attachment.userDateLocalDate,
        userNote: attachment.userNote,
      });
    }
    // eslint-disable-next-line
  }, [visible, attachment]);

  const formNames = proxiedPropertiesOf<AttachmentUploadedOutDTO>();
  const formFieldName = (name: keyof AttachmentUploadedOutDTO) => name;

  const handleAccessTypeChange = (accessType: AccessTypeEnum) => {
    form.setFieldValue(formFieldName('accessType'), accessType);
  };

  const save = (data: any) => {
    const payloadForApi = {
      attachmentId: attachment.attachmentId,
      accessType: data.accessType,
      documentType: data.documentType?.documentType,
      userDateLocalDate: data.userDateLocalDate,
      userNote: data.userNote,
      labelIds: data.labels ? data.labels.map((l: LabelPreviewWithoutTypeOutDTO) => l.labelId) : [],
    };

    editAttachment(
      attachment.attachmentId,
      payloadForApi,
      () => {
        const changedDtoFields: Partial<AttachmentUploadedOutDTO> = {
          accessType: data.accessType,
          documentType: data.documentType?.documentType,
          documentTypeName: data.documentType?.name,
          userDateLocalDate: data.userDateLocalDate,
          userNote: data.userNote,
        };
        onUpdated({
          attachmentId: attachment.attachmentId,
          changedDtoFields: changedDtoFields,
          newLabelIds: payloadForApi.labelIds,
          newLabels: data.labels || []
        });
        onHide();
      },
    );
  };

  return (
    <RingilSidebar
      visible={visible}
      heading={tr('EditAttachmentSidebar.editDocument', 'Edit document')}
      onContinue={form.validateAndSend}
      onBack={onHide}
      showSaveButton={true}
      continueLabel={tr('EditAttachmentSidebar.save', 'Save')}
      disableSaveButton={false}
    >
      {/* Opts into the legacy PrimeReact overrides in styles/overrides/legacy-fields.scss. */}
      <div className={'r-legacy-calendar-fields contents'}>
      <FileCard>
        <FileHeader>
          <FileName>
            <LinkSvg width={16} height={16} />
            {attachment.fileName}
          </FileName>
        </FileHeader>

        <Field
          label={tr('EditAttachmentSidebar.documentType', 'Document type')}
          required={true}
          errorMessage={findValidationMessage(form.validationErrors, 'documentType.documentType', tr) || findValidationMessage(form.validationErrors, 'documentType', tr)}
        >
          <StyledInputDocumentType
            style={{ width: '100%', fontFamily: "'DM Sans', sans-serif" }}
            documentType={form.values.documentType?.documentType}
            filter
            onChange={v => form.setFieldValue(formFieldName('documentType'), v)}
          />
        </Field>

        <div>
          <FieldLabelRow>
            <FieldLabel>{tr('UploadAttachmentDialog.accessType', 'Visibility')}</FieldLabel>
            <span
              id="accessType-span"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 25,
                height: 25,
                borderRadius: '50%',
                background: '#e7ebee',
                color: '#616061',
                fontWeight: 500,
                fontSize: '1rem',
                marginLeft: 6,
                padding: 10,
              }}
            >
              ?
            </span>
            <CustomTooltip 
              content={tr('UploadAttachmentDialog.sharedDocumentInfo', 'Sdílený dokument je viditelný také vašim partnerům')} 
              targetSelector="#accessType-span" 
            />
          </FieldLabelRow>
          <PillButtonGroup>
            <PillButton type="button" selected={form.values.accessType === AccessTypeEnum.INTERNAL} onClick={() => handleAccessTypeChange(AccessTypeEnum.INTERNAL)}>
              {tr('EditAttachmentSidebar.internal', 'Internal')}
            </PillButton>
            <PillButton type="button" selected={form.values.accessType === AccessTypeEnum.ALL} onClick={() => handleAccessTypeChange(AccessTypeEnum.ALL)}>
              {tr('EditAttachmentSidebar.shared', 'Shared')}
            </PillButton>
          </PillButtonGroup>
        </div>

        <Field label={tr('EditAttachmentSidebar.date', 'Date')}>
          <StyledCalendar
            showIcon
            icon={<AddonsSvg width={20} height={20} />}
            value={form.values.userDateLocalDate ? dayjsFromLocalDateString(form.values.userDateLocalDate).toDate() : null}
            onChange={e => form.setFieldValue(formFieldName('userDateLocalDate'), Array.isArray(e.value) ? null : e.value ? stringYMDFromDate(e.value) : null)}
            dateFormat={shortDateFormat}
            selectionMode={'single'}
            locale="cs"
            hideOnDateTimeSelect={true}
          />
        </Field>

        <div>
          <LabelsTag
            objectId={attachment.attachmentId}
            objectType={EntityTypeEnum.ATTACHMENTS}
            resolveLabelsForType={EntityTypeEnum.ATTACHMENTS}
            disabled={false}
            wrapperWidth='500px'
          />
        </div>

        <Field
          label={tr('EditAttachmentSidebar.note', 'Note')}
          errorMessage={findValidationMessage(form.validationErrors, formFieldName('userNote'), tr)}
        >
          <StyledTextarea value={form.values.userNote} onChange={e => form.setFieldValue(formFieldName('userNote'), e.target.value)} />
        </Field>
        <HorizontalLine variant="tertiary" />
      </FileCard>
      </div>
    </RingilSidebar>
  );
};

export default EditAttachmentSidebar;
